import { useState } from "react";
import Marquee from "react-fast-marquee";

// Brands whose icons were removed from Simple Icons for trademark reasons.
// We render a clean monogram badge for these and skip the network fetch.
const NO_ICON = new Set([
    "youtrack",
    "mondaydotcom",
    "slack",
    "microsoftteams",
    "slab",
    "lucidchart",
    "whimsical",
    "salesforce",
    "pipedrive",
    "close",
    "freshbooks",
    "wave",
    "bamboohr",
    "workday",
    "rippling",
    "deel",
    "harvest",
    "hubstaff",
    "jotform",
    "tableau",
    "mode",
    "powerbi",
    "frontapp",
    "freshworks",
]);

const brands = [
    { name: "Jira", slug: "jira" },
    { name: "ClickUp", slug: "clickup" },
    { name: "Linear", slug: "linear" },
    { name: "Asana", slug: "asana" },
    { name: "YouTrack", slug: "youtrack" },
    { name: "Trello", slug: "trello" },
    { name: "Monday", slug: "mondaydotcom" },
    { name: "Slack", slug: "slack" },
    { name: "Discord", slug: "discord" },
    { name: "MS Teams", slug: "microsoftteams" },
    { name: "Mattermost", slug: "mattermost" },
    { name: "Rocket.Chat", slug: "rocketdotchat" },
    { name: "Notion", slug: "notion" },
    { name: "Confluence", slug: "confluence" },
    { name: "Coda", slug: "coda" },
    { name: "Google Docs", slug: "googledocs" },
    { name: "Slab", slug: "slab", id: "slab" },
    { name: "Miro", slug: "miro" },
    { name: "FigJam", slug: "figma", id: "figjam" },
    { name: "Mural", slug: "mural" },
    { name: "Lucidchart", slug: "lucidchart" },
    { name: "Whimsical", slug: "whimsical" },
    { name: "Salesforce", slug: "salesforce" },
    { name: "HubSpot", slug: "hubspot" },
    { name: "Zoho CRM", slug: "zoho", id: "zoho-crm" },
    { name: "Pipedrive", slug: "pipedrive" },
    { name: "Close", slug: "close" },
    { name: "QuickBooks", slug: "quickbooks" },
    { name: "Xero", slug: "xero" },
    { name: "Zoho Invoice", slug: "zoho", id: "zoho-invoice" },
    { name: "FreshBooks", slug: "freshbooks" },
    { name: "Wave", slug: "wave" },
    { name: "BambooHR", slug: "bamboohr" },
    { name: "Workday", slug: "workday" },
    { name: "Gusto", slug: "gusto" },
    { name: "Rippling", slug: "rippling" },
    { name: "Deel", slug: "deel" },
    { name: "Toggl", slug: "toggl" },
    { name: "Harvest", slug: "harvest" },
    { name: "Clockify", slug: "clockify" },
    { name: "Hubstaff", slug: "hubstaff" },
    { name: "RescueTime", slug: "rescuetime" },
    { name: "Zapier", slug: "zapier" },
    { name: "Make", slug: "make" },
    { name: "n8n", slug: "n8n" },
    { name: "Typeform", slug: "typeform" },
    { name: "Jotform", slug: "jotform" },
    { name: "Tableau", slug: "tableau" },
    { name: "Looker", slug: "looker" },
    { name: "Metabase", slug: "metabase" },
    { name: "Mode", slug: "mode" },
    { name: "Power BI", slug: "powerbi" },
    { name: "Front", slug: "frontapp" },
    { name: "Help Scout", slug: "helpscout" },
    { name: "Intercom", slug: "intercom" },
    { name: "Zendesk", slug: "zendesk" },
    { name: "Freshdesk", slug: "freshworks", id: "freshdesk" },
];

const BrandChip = ({ name, slug, id }) => {
    const testId = id || slug;
    const skipFetch = NO_ICON.has(slug);
    const [iconOk, setIconOk] = useState(!skipFetch);
    // First letter as fallback monogram
    const monogram = name.replace(/[^A-Za-z]/g, "").slice(0, 1).toUpperCase();
    return (
        <div
            className="inline-flex items-center gap-3 mx-2 px-4 py-2.5 rounded-xl bg-white/[0.03] hover:bg-white/[0.08] border border-white/[0.07] hover:border-white/20 transition-colors duration-200 backdrop-blur-sm whitespace-nowrap group"
            data-testid={`brand-chip-${testId}`}
        >
            {iconOk ? (
                <img
                    src={`https://cdn.simpleicons.org/${slug}/e5e5e5`}
                    alt=""
                    aria-hidden
                    loading="lazy"
                    className="w-[24px] h-[24px] opacity-85 group-hover:opacity-100 transition-opacity"
                    onError={() => setIconOk(false)}
                />
            ) : (
                <span
                    className="w-[24px] h-[24px] rounded-md bg-white/[0.06] border border-white/10 flex items-center justify-center font-display text-[12px] font-semibold text-zinc-400 group-hover:text-white group-hover:bg-white/10 transition-colors"
                    aria-hidden
                >
                    {monogram}
                </span>
            )}
            <span className="font-display text-[15px] font-medium text-zinc-300 group-hover:text-white transition-colors">
                {name}
            </span>
        </div>
    );
};

export const MarqueeStrip = () => {
    const perRow = Math.ceil(brands.length / 5);
    const row1 = brands.slice(0, perRow);
    const row2 = brands.slice(perRow, perRow * 2);
    const row3 = brands.slice(perRow * 2, perRow * 3);
    const row4 = brands.slice(perRow * 3, perRow * 4);
    const row5 = brands.slice(perRow * 4);

    return (
        <section
            className="relative py-16 border-y border-white/[0.06] bg-obsidian-300 overflow-hidden"
            data-testid="marquee-section"
        >
            <div className="absolute inset-0 grid-bg opacity-[0.18] pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
                <div className="text-center mb-10">
                    <p className="font-code text-[11px] uppercase tracking-[0.3em] text-zinc-600 font-semibold mb-3">
                        // replace fragmented stacks
                    </p>
                    <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white">
                        One workspace.{" "}
                        <span className="text-zinc-500">Every function.</span>
                    </h2>
                </div>

                <div className="relative space-y-3.5 ticker-mask">
                    <Marquee gradient={false} speed={32} pauseOnHover>
                        {row1.map((b) => (
                            <BrandChip key={`r1-${b.name}`} {...b} />
                        ))}
                    </Marquee>
                    <Marquee
                        gradient={false}
                        speed={28}
                        pauseOnHover
                        direction="right"
                    >
                        {row2.map((b) => (
                            <BrandChip key={`r2-${b.name}`} {...b} />
                        ))}
                    </Marquee>
                    <Marquee gradient={false} speed={30} pauseOnHover>
                        {row3.map((b) => (
                            <BrandChip key={`r3-${b.name}`} {...b} />
                        ))}
                    </Marquee>
                    <Marquee
                        gradient={false}
                        speed={26}
                        pauseOnHover
                        direction="right"
                    >
                        {row4.map((b) => (
                            <BrandChip key={`r4-${b.name}`} {...b} />
                        ))}
                    </Marquee>
                    <Marquee gradient={false} speed={34} pauseOnHover>
                        {row5.map((b) => (
                            <BrandChip key={`r5-${b.name}`} {...b} />
                        ))}
                    </Marquee>
                </div>

                <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 font-code text-xs">
                    <span className="text-zinc-500">
                        <span className="text-rose-300/80 line-through decoration-zinc-700">
                            {brands.length}+ tools you stop paying for
                        </span>
                    </span>
                    <span className="text-zinc-700">→</span>
                    <span className="flex items-center gap-2 text-emerald-300">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow" />
                        ApexBrain · one workspace
                    </span>
                </div>
            </div>
        </section>
    );
};
